import React, {
  ReactPortal,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import useLexicalEditable from '@lexical/react/useLexicalEditable';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  TableCellNode,
  TableNode,
  $getTableCellNodeFromLexicalNode,
  $getTableNodeFromLexicalNodeOrThrow,
  $insertTableRow,
  $getTableRowIndexFromTableCellNode,
  $getElementGridForTableNode,
  $insertTableColumn,
  $getTableColumnIndexFromTableCellNode,
  $deleteTableColumn,
  $removeTableRowAtIndex,
  $isTableCellNode,
} from '@lexical/table';
import {
  $createParagraphNode,
  $getPreviousSelection,
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  $isRootNode,
  $isTextNode,
  COMMAND_PRIORITY_HIGH,
  DEPRECATED_$isGridSelection,
  KEY_TAB_COMMAND,
} from 'lexical';
import { Icon } from 'semantic-ui-react';

type TableCellActionMenuProps = {
  anchorElem: HTMLElement;
  close: () => void;
  tableCellNode: TableCellNode;
};

function TableCellActionMenu({
  anchorElem,
  close,
  tableCellNode,
}: TableCellActionMenuProps) {
  const dropDownRef = useRef<HTMLDivElement | null>(null);
  const [editor] = useLexicalComposerContext();

  const [selectionCounts, updateSelectionCounts] = useState({
    columns: 1,
    rows: 1,
  });

  useEffect(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();

      if (DEPRECATED_$isGridSelection(selection)) {
        const selectionShape = selection.getShape();

        updateSelectionCounts({
          columns: selectionShape.toX - selectionShape.fromX + 1,
          rows: selectionShape.toY - selectionShape.fromY + 1,
        });
      }
    });
  }, [editor]);

  const insertRowAtSelection = useCallback(
    (shouldInsertAfter: boolean = true) => {
      editor.update(() => {
        const selection = $getSelection();
        const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);

        let tableRowIndex;

        if (DEPRECATED_$isGridSelection(selection)) {
          const selectionShape = selection.getShape();
          tableRowIndex = shouldInsertAfter
            ? selectionShape.toY
            : selectionShape.fromY;
        } else {
          tableRowIndex = $getTableRowIndexFromTableCellNode(tableCellNode);
        }

        const grid = $getElementGridForTableNode(editor, tableNode);

        if (tableNode) {
          $insertTableRow(
            tableNode,
            tableRowIndex,
            shouldInsertAfter,
            selectionCounts.rows,
            grid,
          );
        }

        close();
      });
    },
    [editor, tableCellNode, selectionCounts],
  );

  const insertColumnAtSelection = useCallback(
    (shouldInsertAfter: boolean = true) => {
      editor.update(() => {
        const selection = $getSelection();
        const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);

        let tableColumnIndex;

        if (DEPRECATED_$isGridSelection(selection)) {
          const selectionShape = selection.getShape();
          tableColumnIndex = shouldInsertAfter
            ? selectionShape.toX
            : selectionShape.fromX;
        } else {
          tableColumnIndex =
            $getTableColumnIndexFromTableCellNode(tableCellNode);
        }

        const grid = $getElementGridForTableNode(editor, tableNode);

        if (tableNode) {
          $insertTableColumn(
            tableNode,
            tableColumnIndex,
            shouldInsertAfter,
            selectionCounts.columns,
            grid,
          );
        }

        close();
      });
    },
    [editor, tableCellNode, selectionCounts],
  );

  const deleteTableRowAtSelection = useCallback(() => {
    editor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
      const tableRowIndex = $getTableRowIndexFromTableCellNode(tableCellNode);

      $removeTableRowAtIndex(tableNode, tableRowIndex);

      close();
    });
  }, [editor, tableCellNode]);

  const deleteTableColumnAtSelection = useCallback(() => {
    editor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);

      const tableColumnIndex =
        $getTableColumnIndexFromTableCellNode(tableCellNode);

      $deleteTableColumn(tableNode, tableColumnIndex);

      close();
    });
  }, [editor, tableCellNode]);

  return createPortal(
    <div
      className="iva-editor-table-cell-action-menu-dropdown"
      ref={dropDownRef}
    >
      <div
        className="iva-editor-table-cell-action-menu-dropdown-item"
        onClick={(e) => {
          e.stopPropagation();
          insertRowAtSelection(false);
        }}
      >
        Insert row above
      </div>
      <div
        className="iva-editor-table-cell-action-menu-dropdown-item"
        onClick={(e) => {
          e.stopPropagation();
          insertRowAtSelection(true);
        }}
      >
        Insert row below
      </div>
      <div
        className="iva-editor-table-cell-action-menu-dropdown-item"
        onClick={(e) => {
          e.stopPropagation();
          deleteTableRowAtSelection();
        }}
      >
        Delete row
      </div>
      <div
        className="iva-editor-table-cell-action-menu-dropdown-item"
        onClick={(e) => {
          e.stopPropagation();
          insertColumnAtSelection(false);
        }}
      >
        Insert column left
      </div>
      <div
        className="iva-editor-table-cell-action-menu-dropdown-item"
        onClick={(e) => {
          e.stopPropagation();
          insertColumnAtSelection(true);
        }}
      >
        Insert column right
      </div>
      <div
        className="iva-editor-table-cell-action-menu-dropdown-item"
        onClick={(e) => {
          e.stopPropagation();
          deleteTableColumnAtSelection();
        }}
      >
        Delete column
      </div>
    </div>,
    anchorElem,
  );
}

export function TableCellActionMenuContainer({
  anchorElem,
}: {
  anchorElem?: HTMLElement | null;
}) {
  const [editor] = useLexicalComposerContext();
  const [tableCellNode, setTableCellNode] = useState<TableCellNode | null>(
    null,
  );
  const menuRef = useRef<HTMLDivElement>(null);
  const [openMenu, setOpenMenu] = useState(false);

  useEffect(() => {
    // place the menu at cell node
    const menuDOM = menuRef.current;
    setOpenMenu(false);

    if (menuDOM && tableCellNode && anchorElem) {
      const tableCellDOM = editor.getElementByKey(tableCellNode.getKey());

      if (tableCellDOM) {
        const cellRect = tableCellDOM.getBoundingClientRect();
        const menuRect = menuDOM.getBoundingClientRect();
        const anchorRect = anchorElem.getBoundingClientRect();

        const top = cellRect.top - anchorRect.top;
        const left = cellRect.right - menuRect.width + 4 - anchorRect.left;

        menuDOM.style.opacity = '1';
        menuDOM.style.transform = `translate(${left}px, ${top}px)`;
      } else {
        menuDOM.style.opacity = '0';
        menuDOM.style.transform = 'translate(-10000px, -10000px)';
      }
    }
  }, [tableCellNode, anchorElem, editor, menuRef.current]);

  useEffect(() => {
    // get the active cell node
    return editor.registerUpdateListener(() => {
      editor.getEditorState().read(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          const tableCellNodeFromSelection = $getTableCellNodeFromLexicalNode(
            selection.anchor.getNode(),
          );
          setTableCellNode(tableCellNodeFromSelection);
        }
      });
    });
  }, [editor]);

  useEffect(() => {
    return editor.registerCommand(
      KEY_TAB_COMMAND,
      (payload) => {
        editor.update(() => {
          const selection = $getSelection();
          const preSelection = $getPreviousSelection();

          if ($isRangeSelection(selection) && $isRangeSelection(preSelection)) {
            const root = selection.anchor.getNode();

            if ($isRootNode(root)) {
              const prevNode = preSelection.anchor.getNode().getParent();

              if ($isTableCellNode(prevNode)) {
                root.append($createParagraphNode());
              } else {
                if (
                  $isTextNode(preSelection.anchor.getNode()) &&
                  $isTableCellNode(
                    preSelection.anchor.getNode().getParent()?.getParent(),
                  )
                ) {
                  root.append($createParagraphNode());
                }
              }
            }
          }
        });
        return false;
      },
      COMMAND_PRIORITY_HIGH,
    );
  }, [editor]);

  return (
    <React.Fragment>
      {tableCellNode && (
        <div
          className="iva-editor-table-cell-action-button-container"
          ref={menuRef}
        >
          <Icon
            name="angle down"
            className="cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              setOpenMenu((isOpen) => !isOpen);
            }}
          />
          {menuRef.current && openMenu && (
            <TableCellActionMenu
              anchorElem={menuRef.current}
              close={() => setOpenMenu(false)}
              tableCellNode={tableCellNode}
            />
          )}
        </div>
      )}
    </React.Fragment>
  );
}

export default function TableActionMenuPlugin({
  anchorElem = document.body,
}: {
  anchorElem?: HTMLElement;
}): null | ReactPortal {
  const isEditable = useLexicalEditable();
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editor.registerMutationListener(TableNode, (nodes, payload) => {
      const tableIterator = Array.from(nodes);
      for (const tableItem of tableIterator) {
        if (tableItem[1] === 'updated') {
          const tableHTML = editor.getElementByKey(
            tableItem[0],
          ) as HTMLTableElement;

          if (
            tableHTML.rows.length === 1 &&
            tableHTML.rows[0]?.firstChild instanceof HTMLBRElement
          ) {
            const tableNode = $getNodeByKey(
              tableItem[0],
              editor.getEditorState(),
            );

            editor.update(() => {
              tableNode?.remove();
            });
          }
        }
      }
    });
  }, [editor]);

  return createPortal(
    isEditable ? (
      <TableCellActionMenuContainer anchorElem={anchorElem} />
    ) : null,
    anchorElem,
  );
}
